import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Checkbox } from '../../../components/ui/checkbox';
import { FolderOpen, Plus, Pencil, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import type { ExpenseCategory } from '../../../types/car';
import { useLanguage } from '../../../contexts/LanguageContext';

export function ExpenseCategoryList() {
  const { t } = useLanguage();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [editCategory, setEditCategory] = useState<ExpenseCategory | null>(null);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<ExpenseCategory | null>(null);
  const [deleteDeleting, setDeleteDeleting] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/expense-categories');
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await api.post('/api/expense-categories', { name, active: true });
      setNewCategoryName('');
      toast.success(t('expense.categoryCreated'));
      fetchCategories();
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('expense.createError'));
    }
  };

  const openEditDialog = (category: ExpenseCategory) => {
    setEditCategory(category);
    setEditName(category.name);
    setEditActive(category.active);
  };

  const closeEditDialog = () => {
    setEditCategory(null);
    setEditName('');
    setEditActive(true);
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCategory) return;
    const name = editName.trim();
    if (!name) return;
    setEditSaving(true);
    try {
      await api.put(`/api/expense-categories/${editCategory.id}`, { name, active: editActive });
      closeEditDialog();
      toast.success(t('expense.categoryUpdated'));
      fetchCategories();
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('expense.updateError'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCategory) return;
    setDeleteDeleting(true);
    try {
      await api.delete(`/api/expense-categories/${deleteCategory.id}`);
      setDeleteCategory(null);
      toast.success(t('expense.categoryDeleted'));
      fetchCategories();
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('expense.deleteError'));
    } finally {
      setDeleteDeleting(false);
    }
  };

  const closeDeleteDialog = () => {
    if (!deleteDeleting) setDeleteCategory(null);
  };

  return (
    <Card className="rounded-2xl border border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <FolderOpen className="h-5 w-5" />
          {t('expense.categoriesTitle')}
        </CardTitle>
        <CardDescription className="text-slate-500">
          {t('expense.categoriesSubtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleCreateCategory} className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px] space-y-2">
            <Label htmlFor="newCategory">{t('expense.newCategory')}</Label>
            <Input
              id="newCategory"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={t('expense.categoryName')}
              className="border-slate-200"
            />
          </div>
          <Button
            type="submit"
            disabled={!newCategoryName.trim()}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 border border-slate-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('expense.addCategory')}
          </Button>
        </form>

        {loading ? (
          <p className="text-sm text-slate-500">{t('common.loading')}</p>
        ) : categories.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
            <p className="text-slate-500">{t('expense.noCategoriesYet')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.categoryName')}</TableHead>
                <TableHead className="text-slate-600 font-medium text-sm">{t('expense.status')}</TableHead>
                <TableHead className="w-[120px] text-right text-slate-600 font-medium text-sm">{t('expense.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50">
                  <TableCell className="text-sm text-slate-700 font-medium">{c.name}</TableCell>
                  <TableCell className="text-sm text-slate-700">{c.active ? t('expense.active') : t('expense.inactive')}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-200"
                        onClick={() => openEditDialog(c)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        {t('expense.edit')}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => setDeleteCategory(c)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Edit category dialog */}
      <Dialog open={!!editCategory} onOpenChange={(open) => !open && closeEditDialog()}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('expense.editCategory')}</DialogTitle>
            <DialogDescription>{t('expense.editCategoryDesc')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCategory} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editCategoryName">{t('expense.categoryName')}</Label>
              <Input
                id="editCategoryName"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('expense.categoryName')}
                className="border-slate-200"
                required
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="editCategoryActive"
                checked={editActive}
                onCheckedChange={(checked) => setEditActive(checked === true)}
              />
              <Label htmlFor="editCategoryActive" className="text-sm font-normal cursor-pointer">
                {t('expense.activeShowInForm')}
              </Label>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeEditDialog} className="border-slate-200">
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={editSaving || !editName.trim()}
                className="bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
              >
                {editSaving ? t('expense.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete category dialog */}
      <Dialog open={!!deleteCategory} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('expense.deleteCategoryConfirm')}</DialogTitle>
            <DialogDescription>
              &quot;{deleteCategory?.name}&quot; will be permanently deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deleteDeleting}
              className="border-slate-200"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteDeleting}
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
            >
              {deleteDeleting ? t('expense.deleting') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
